import { isUndefined } from 'lodash';
import { first } from 'rxjs/operators';

import { connect } from '@swf/core/api/connect';
import { FeatureFlag } from '@swf/core/enums';
import type { EffectParams } from '@swf/core/store';
import { select } from '@swf/core/store';
import { License } from '@swf/core/types/Account';
import {
  clearCookies,
  clearRemainigInactivitySeconds,
  clearSession,
  clearSessionSummary
} from '@swf/core/utils/auth';

import { unsubscribe } from './utils';

const logout = async (
  payload: { sessionExpired?: boolean },
  { rootState, dispatch, request }: EffectParams
) => {
  const isAuthenticated = select.session.isAuthenticated(rootState);
  clearCookies(rootState);
  clearRemainigInactivitySeconds();
  clearSessionSummary();
  unsubscribe();

  try {
    if (isAuthenticated) {
      dispatch.modal.clear();
      if (!payload.sessionExpired) {
        const license = select.account.getLicense(rootState);
        const isJWTEnabled = select.feature.check(rootState, FeatureFlag.JWTEnabled);
        const options: Parameters<typeof request>[0] = { service: 'session.logout' };

        if (isJWTEnabled) {
          options.service = '/api/v1-preview/auth/session';
          options.http = true;
          options.method = 'DELETE';
        }

        const { body } = await request(options);

        if (select.account.requireDocumentValidation(rootState)) {
          dispatch.modal.enqueue({ path: '/notifications/documents-verification' });
        }

        switch (license) {
          case License.IT:
            dispatch.modal.enqueue({
              path: '/notifications/logout-summary',
              locationState: { data: body.playingInfo }
            });
            break;
          case License.USNJ:
          case License.USPA:
          case License.USMI:
            dispatch.modal.enqueue({ path: '/notifications/logout-summary' });
            break;
          case License.ES:
          case License.PT:
            dispatch.modal.enqueue({
              path: '/notifications/account-information',
              locationState: body.playingInfo
            });
            break;
          case License.SE:
            dispatch.modal.enqueue({
              path: '/notifications/login-information',
              locationState: { data: body.sessionWinLossData, license }
            });
            break;
          case License.GR: {
            if (select.account.tempAccount(rootState)) {
              dispatch.modal.enqueue({ path: '/notifications/validate-account-reminder' });
            }
            break;
          }
          case License.CZ:
          case License.RO: {
            const { validateAccountDays } = select.localSettings.get(rootState);
            if (!isUndefined(validateAccountDays)) {
              dispatch.localSettings.set({ validateAccountDays: undefined });
              dispatch.modal.enqueue({
                path: '/notifications/validate-account-reminder',
                locationState: { param: validateAccountDays }
              });
            }
            break;
          }
        }
      }
    }
  } catch (e) {
    // do nothing
  } finally {
    connect()
      .pipe(first())
      .subscribe(async ({ client }) => {
        if (client) {
          client.ws.close();
        }
        clearSession(dispatch);
        await dispatch.session.fetch();
        await dispatch.feature.check();
      });
  }
};

export default logout;
